import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import {
  createReportPdf, preloadPdfLogo, drawSectionBanner, drawKpiTiles, drawCallout,
  drawTable, drawEmptyState, drawClosingFooter, finalizeFooters, savePdf,
  fmtGeneratedAt, severityCell, statusCell, COLORS,
  type DocCtx, type KpiTile, type TableCell,
} from '../lib/pdfTemplate';
import { fmtTs, getTz } from '../lib/globalTz';

type Priority = 'Immediate' | 'High' | 'Medium' | 'Low';

type ExecutivePayload = {
  ok: true;
  customerId: string;
  lookback: string;
  generatedAt: string;
  expiresAt: string | null;
  cacheHit?: boolean;
  posture: { score: number; level: string; summary: string; secureScore: string; openHighCritical: number; activeIncidents: number };
  monthlyIncidentTrends: Array<{ month: string; total: number; highCritical: number; closed: number }>;
  majorIncidents: Array<{ incidentNumber: string; title: string; severity: string; status: string; createdTime: string; owner?: string | null }>;
  slaPerformance: { metPercent: number; breachedCount: number; atRiskCount: number; summary: string };
  exposure: { exposureScore: number | null; affectedDevices: number; criticalCves: number; highCves: number; backlogTotal: number; summary: string };
  recommendations: Array<{ priority: Priority; title: string; details: string }>;
  businessRiskSummary: string;
  improvementOverTime: { direction: 'Improving' | 'Stable' | 'Worsening'; summary: string; previousHighCritical: number; currentHighCritical: number };
  diagnostics: string[];
};

function scoreTone(score: number): string {
  if (score >= 80) return 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-400 dark:bg-emerald-500/10 dark:text-emerald-200';
  if (score >= 60) return 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-400 dark:bg-amber-500/10 dark:text-amber-200';
  if (score >= 40) return 'border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-400 dark:bg-orange-500/10 dark:text-orange-200';
  return 'border-red-300 bg-red-50 text-red-700 dark:border-red-400 dark:bg-red-500/10 dark:text-red-200';
}

function priorityClass(priority: Priority): string {
  if (priority === 'Immediate') return 'border-red-300 bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-200 dark:border-red-900';
  if (priority === 'High') return 'border-orange-300 bg-orange-50 text-orange-700 dark:bg-orange-950/30 dark:text-orange-200 dark:border-orange-900';
  if (priority === 'Medium') return 'border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900';
  return 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200 dark:border-emerald-900';
}

function barWidth(value: number, max: number): string {
  if (max <= 0) return '0%';
  return `${Math.max(3, Math.min(100, Math.round((value / max) * 100)))}%`;
}

function text(value: unknown, max = 180): string {
  if (value === null || value === undefined) return '';
  const raw = typeof value === 'string' ? value : JSON.stringify(value);
  const clean = raw.replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

function priorityCell(priority: Priority): TableCell {
  const fill = priority === 'Immediate' ? COLORS.danger
    : priority === 'High' ? COLORS.warning
    : priority === 'Medium' ? COLORS.riskModerateBg
    : COLORS.riskLowBg;
  const fg = priority === 'Immediate' || priority === 'High' ? COLORS.white
    : priority === 'Medium' ? COLORS.riskModerateFg
    : COLORS.riskLowFg;
  return { content: priority, styles: { fillColor: fill, textColor: fg, fontStyle: 'bold', halign: 'center', valign: 'middle' } };
}

async function exportPdf(payload: ExecutivePayload, customerName: string, lookback: string, filenameBase: string): Promise<void> {
  await preloadPdfLogo();
  const tz = getTz();
  const ctx = createReportPdf({
    title: 'Executive Security Brief',
    customerName,
    periodLabel: `${lookback} lookback`,
    generatedLabel: fmtGeneratedAt(payload.generatedAt, tz),
  });

  drawSectionBanner(ctx, 'Executive Overview', 'primary');
  const postureTone: KpiTile['tone'] = payload.posture.score >= 80 ? 'success' : payload.posture.score >= 60 ? 'warning' : payload.posture.score >= 40 ? 'warning' : 'danger';
  const tiles: KpiTile[] = [
    { value: String(payload.posture.score), label: `Security Posture (${payload.posture.level})`, tone: postureTone },
    { value: `${payload.slaPerformance.metPercent}%`, label: 'SLA Performance', sublabel: `${payload.slaPerformance.breachedCount} breached · ${payload.slaPerformance.atRiskCount} at risk`, tone: payload.slaPerformance.metPercent >= 90 ? 'success' : payload.slaPerformance.metPercent >= 70 ? 'warning' : 'danger' },
    { value: payload.exposure.exposureScore === null ? '-' : String(payload.exposure.exposureScore), label: 'Exposure Score', sublabel: `${payload.exposure.affectedDevices} affected devices`, tone: 'info' },
    { value: String(payload.posture.openHighCritical), label: 'Open High/Critical', sublabel: `${payload.posture.activeIncidents} active incidents`, tone: payload.posture.openHighCritical > 0 ? 'danger' : 'success' },
  ];
  drawKpiTiles(ctx, tiles);

  drawCallout(ctx, { tone: 'neutral', title: 'Business Risk Summary', body: payload.businessRiskSummary });
  drawCallout(ctx, {
    tone: payload.improvementOverTime.direction === 'Improving' ? 'success' : payload.improvementOverTime.direction === 'Worsening' ? 'critical' : 'info',
    title: `Improvement Over Time: ${payload.improvementOverTime.direction}`,
    body: payload.improvementOverTime.summary,
  });
  drawCallout(ctx, { tone: 'info', title: 'Vulnerability Backlog', body: payload.exposure.summary });

  drawSectionBanner(ctx, 'Monthly Incident Trends', 'primary');
  if (payload.monthlyIncidentTrends.length) {
    const trendRows: TableCell[][] = payload.monthlyIncidentTrends.map((row) => [
      row.month,
      String(row.total),
      String(row.highCritical),
      String(row.closed),
    ]);
    drawTable(ctx, {
      head: ['Month', 'Total', 'High/Crit', 'Closed'],
      body: trendRows,
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 34, halign: 'center' },
        2: { cellWidth: 34, halign: 'center' },
        3: { cellWidth: 34, halign: 'center' },
      },
      minRowHeight: 8,
    });
  } else {
    drawEmptyState(ctx, 'No incident trend data available.');
  }

  drawSectionBanner(ctx, 'Major Incidents', 'orange');
  if (payload.majorIncidents.length) {
    const incidentRows: TableCell[][] = payload.majorIncidents.map((incident) => [
      text(incident.incidentNumber, 14),
      text(incident.title, 60),
      severityCell(incident.severity),
      statusCell(incident.status),
      text(incident.owner ?? '-', 26),
    ]);
    drawTable(ctx, {
      head: ['#', 'Title', 'Severity', 'Status', 'Owner'],
      body: incidentRows,
      columnStyles: {
        0: { cellWidth: 18, halign: 'center' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 22, halign: 'center' },
        3: { cellWidth: 22, halign: 'center' },
        4: { cellWidth: 26 },
      },
      minRowHeight: 8,
    });
  } else {
    drawEmptyState(ctx, 'No major incidents in the selected lookback.');
  }

  drawSectionBanner(ctx, 'Executive Recommendations', 'primary');
  if (payload.recommendations.length) {
    const recRows: TableCell[][] = payload.recommendations.map((rec) => [
      priorityCell(rec.priority),
      text(rec.title, 45),
      text(rec.details, 150),
    ]);
    drawTable(ctx, {
      head: ['Priority', 'Title', 'Details'],
      body: recRows,
      columnStyles: {
        0: { cellWidth: 22, halign: 'center' },
        1: { cellWidth: 45 },
        2: { cellWidth: 'auto' },
      },
      minRowHeight: 8,
    });
  } else {
    drawEmptyState(ctx, 'No executive recommendations for the selected lookback.');
  }

  drawClosingFooter(ctx);
  finalizeFooters(ctx);
  savePdf(ctx, filenameBase);
}

interface CustomerExecutivePortalTabProps {
  customerId?: string;
  customerName?: string;
}

export function CustomerExecutivePortalTab(props: CustomerExecutivePortalTabProps) {
  const { activeCustomerId, activeCustomer } = useCustomers();
  const { canCapability } = useAuth();
  const scopedCustomerId = props.customerId ?? (activeCustomerId !== 'default' ? activeCustomerId : activeCustomer?.id);
  const customerName = props.customerName ?? activeCustomer?.name ?? scopedCustomerId ?? 'Customer';
  const [lookback, setLookback] = useState<'30d' | '60d' | '90d'>('90d');
  const [payload, setPayload] = useState<ExecutivePayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canRead = canCapability('customer_exec', 'read');
  const canRefresh = canCapability('customer_exec', 'refresh');
  const canDownload = canCapability('customer_exec', 'download_report');
  const maxTrend = useMemo(() => Math.max(1, ...(payload?.monthlyIncidentTrends ?? []).map((row) => Math.max(row.total, row.highCritical, row.closed))), [payload]);

  useEffect(() => {
    if (scopedCustomerId && canRead) void load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedCustomerId, lookback, canRead]);

  async function load(refresh: boolean) {
    if (!scopedCustomerId) {
      setError('Select a customer to load the Executive Portal.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<ExecutivePayload>(`/customer-executive/summary?customerId=${encodeURIComponent(scopedCustomerId)}&lookback=${encodeURIComponent(lookback)}${refresh ? '&refresh=1' : ''}`);
      setPayload(data);
      toast({ title: data.cacheHit ? 'Executive Portal loaded from cache' : 'Executive Portal refreshed', description: `${data.posture.level} posture · score ${data.posture.score}` });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  async function downloadReport() {
    if (!scopedCustomerId || !payload) return;
    setReportBusy(true);
    setError(null);
    try {
      await exportPdf(payload, customerName, lookback, `kloudsecure-executive-brief-${scopedCustomerId}`);
      toast({ title: 'Executive report downloaded', description: customerName });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setReportBusy(false);
    }
  }

  if (!canRead) {
    return <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900">Executive Portal is hidden by Permissions Master. Enable Executive Portal → Read for this role.</div>;
  }

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground font-bold">Customer-Facing Executive Portal</div>
            <h2 className="text-2xl font-black text-foreground">Executive Security View</h2>
            <p className="text-sm text-muted-foreground max-w-4xl">Read-only leadership view for {customerName}. It intentionally hides analyst noise and focuses on posture, monthly trends, major incidents, SLA, exposure, recommendations and business risk.</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Lookback</label>
              <select value={lookback} onChange={(event) => setLookback(event.target.value as '30d' | '60d' | '90d')} className="mt-1 rounded-xl border border-border bg-background px-3 py-2 text-sm">
                <option value="30d">30 days</option>
                <option value="60d">60 days</option>
                <option value="90d">90 days</option>
              </select>
            </div>
            <button type="button" onClick={() => void load(false)} disabled={loading || !scopedCustomerId} className="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-primary-foreground hover:opacity-90 disabled:opacity-60">{loading ? 'Loading…' : 'Load'}</button>
            {canRefresh && <button type="button" onClick={() => void load(true)} disabled={loading || !scopedCustomerId} className="rounded-xl border border-border px-4 py-2 text-sm font-bold hover:bg-muted disabled:opacity-60">Refresh</button>}
            {canDownload && payload && <button type="button" onClick={() => void downloadReport()} disabled={reportBusy} className="rounded-xl border border-border px-4 py-2 text-sm font-bold hover:bg-muted disabled:opacity-60">{reportBusy ? 'Preparing…' : 'Download Report'}</button>}
          </div>
        </div>
        {payload && <div className="mt-3 text-xs text-muted-foreground">Generated {fmtTs(payload.generatedAt)} · {payload.cacheHit ? 'Loaded from DB cache' : 'Freshly queried'}{payload.expiresAt ? ` · Cache expires ${fmtTs(payload.expiresAt)}` : ''}</div>}
        {error && <div className="mt-4 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-200 dark:border-red-900">{error}</div>}
      </div>

      {!scopedCustomerId && <div className="rounded-2xl border border-dashed border-border bg-card/60 p-10 text-center text-muted-foreground">Select a customer to load the Executive Portal.</div>}

      {payload && (
        <>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div className={`rounded-2xl border p-5 shadow-sm ${scoreTone(payload.posture.score)}`}><div className="text-xs uppercase font-bold">Security Posture</div><div className="mt-2 text-4xl font-black">{payload.posture.score}</div><div className="font-bold">{payload.posture.level}</div></div>
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm"><div className="text-xs uppercase text-muted-foreground font-bold">SLA Performance</div><div className="mt-2 text-4xl font-black text-foreground">{payload.slaPerformance.metPercent}%</div><div className="text-xs text-muted-foreground">{payload.slaPerformance.breachedCount} breached · {payload.slaPerformance.atRiskCount} at risk</div></div>
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm"><div className="text-xs uppercase text-muted-foreground font-bold">Exposure Score</div><div className="mt-2 text-4xl font-black text-foreground">{payload.exposure.exposureScore ?? '-'}</div><div className="text-xs text-muted-foreground">{payload.exposure.affectedDevices} affected devices</div></div>
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm"><div className="text-xs uppercase text-muted-foreground font-bold">Open High/Critical</div><div className="mt-2 text-4xl font-black text-foreground">{payload.posture.openHighCritical}</div><div className="text-xs text-muted-foreground">{payload.posture.activeIncidents} active incidents</div></div>
          </div>

          <div className="grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <h3 className="text-lg font-black text-foreground">Business Risk Summary</h3>
              <p className="mt-2 text-sm text-muted-foreground">{payload.businessRiskSummary}</p>
              <div className="mt-4 rounded-xl border border-border bg-background p-3 text-sm text-muted-foreground"><strong className="text-foreground">Improvement over time:</strong> {payload.improvementOverTime.summary}</div>
              <div className="mt-4 rounded-xl border border-border bg-background p-3 text-sm text-muted-foreground"><strong className="text-foreground">Vulnerability backlog:</strong> {payload.exposure.summary}</div>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <h3 className="text-lg font-black text-foreground">Monthly Incident Trends</h3>
              <div className="mt-4 space-y-3">
                {payload.monthlyIncidentTrends.map((row) => (
                  <div key={row.month} className="grid gap-2 md:grid-cols-[80px_1fr_70px] items-center text-sm">
                    <div className="font-bold text-foreground">{row.month}</div>
                    <div className="space-y-1">
                      <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bg-primary" style={{ width: barWidth(row.total, maxTrend) }} /></div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bg-red-500" style={{ width: barWidth(row.highCritical, maxTrend) }} /></div>
                    </div>
                    <div className="text-xs text-muted-foreground">{row.total} total<br />{row.highCritical} high/crit</div>
                  </div>
                ))}
                {payload.monthlyIncidentTrends.length === 0 && <div className="text-sm text-muted-foreground">No incident trend data available.</div>}
              </div>
            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <h3 className="text-lg font-black text-foreground">Major Incidents</h3>
              <div className="mt-4 overflow-auto rounded-xl border border-border">
                <table className="min-w-full text-sm"><thead className="bg-muted/60 text-muted-foreground"><tr><th className="px-3 py-2 text-left">#</th><th className="px-3 py-2 text-left">Title</th><th className="px-3 py-2 text-left">Severity</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Owner</th></tr></thead><tbody>
                  {payload.majorIncidents.map((incident) => <tr key={`${incident.incidentNumber}-${incident.title}`} className="border-t border-border"><td className="px-3 py-2">{incident.incidentNumber}</td><td className="px-3 py-2 font-medium text-foreground">{incident.title}</td><td className="px-3 py-2">{incident.severity}</td><td className="px-3 py-2">{incident.status}</td><td className="px-3 py-2">{incident.owner ?? '-'}</td></tr>)}
                  {payload.majorIncidents.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">No major incidents in the selected lookback.</td></tr>}
                </tbody></table>
              </div>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <h3 className="text-lg font-black text-foreground">Executive Recommendations</h3>
              <div className="mt-4 space-y-3">
                {payload.recommendations.map((rec) => <div key={`${rec.priority}-${rec.title}`} className="rounded-xl border border-border bg-background p-3"><div className="flex items-center gap-2"><span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${priorityClass(rec.priority)}`}>{rec.priority}</span><span className="font-bold text-foreground">{rec.title}</span></div><p className="mt-2 text-sm text-muted-foreground">{rec.details}</p></div>)}
              </div>
            </div>
          </div>

          {payload.diagnostics.length > 0 && <details className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900"><summary className="cursor-pointer font-bold">Executive Portal diagnostics</summary><ul className="mt-2 list-disc pl-5 space-y-1">{payload.diagnostics.map((d, idx) => <li key={`${d}-${idx}`}>{d}</li>)}</ul></details>}
        </>
      )}
    </div>
  );
}
